import { api } from '@/shared/api/client'
import { qs, type QueryValue } from '@/shared/lib/query'
import type { PageResponse } from '@/shared/api/types'
import type {
  IssuedMeetingLink, JoinOutcome, Meeting, MeetingAnalysis, MeetingLink, MeetingLobby,
  MeetingMessage, MeetingNote, MeetingParticipant, MeetingRecording, MeetingTranscript,
} from './types'

/**
 * Meetings, for people with an account.
 *
 * Guests go through `guestApi.ts`, which is a different client holding a different kind of
 * credential — see its header.
 *
 * Two rules worth knowing before adding to this file:
 *
 * 1. **A meeting outside your scope is a 404, not a 403.** The server answers "no such
 *    meeting" on purpose, and this client must never translate that into "you do not have
 *    permission" — that would confirm the meeting exists.
 * 2. **The lobby is polled.** There is no socket anywhere in this product. Poll
 *    `getLobby` while a meeting is open and stop when the screen is not visible.
 */

export const listMeetings = (params: Record<string, QueryValue>, page = 0, size = 50) =>
  api.get<PageResponse<Meeting>>(`/meetings${qs({ ...params, page, size })}`)

export const getMeeting = (id: string) => api.get<Meeting>(`/meetings/${id}`)

export const createMeeting = (body: unknown) => api.post<Meeting>('/meetings', body)

export const updateMeeting = (id: string, body: unknown) =>
  api.patch<Meeting>(`/meetings/${id}`, body)

export const startMeeting = (id: string) => api.post<Meeting>(`/meetings/${id}/start`)

export const endMeeting = (id: string) => api.post<Meeting>(`/meetings/${id}/end`)

export const cancelMeeting = (id: string, reason: string) =>
  api.post<Meeting>(`/meetings/${id}/cancel`, { reason })

/** Answers `{admissionCode: 'WAITING', grant: null}` while the host has not let you in. */
export const joinMeeting = (id: string) => api.post<JoinOutcome>(`/meetings/${id}/join`)

export const getLobby = (id: string) => api.get<MeetingLobby>(`/meetings/${id}/lobby`)

/** `all` is the admit-everyone button; otherwise name the ids. */
export const admitToMeeting = (id: string, body: { participantIds?: string[]; all?: boolean }) =>
  api.post<MeetingLobby>(`/meetings/${id}/lobby/admit`, body)

export const denyFromMeeting = (id: string, participantIds: string[]) =>
  api.post<MeetingLobby>(`/meetings/${id}/lobby/deny`, { participantIds })

export const listParticipants = (id: string) =>
  api.get<MeetingParticipant[]>(`/meetings/${id}/participants`)

/** The reason is mandatory, and the server agrees. */
export const removeParticipant = (id: string, participantId: string, reason: string) =>
  api.post<MeetingParticipant[]>(`/meetings/${id}/participants/${participantId}/remove`, { reason })

/**
 * Put YOUR OWN hand up, or take it down.
 *
 * There is no participant id: nobody moves anybody else's hand, including the chair. The
 * answer is the whole roster, so one request both sets your hand and refreshes everybody
 * else's — the caller does not have to follow it with a read.
 */
export const raiseHand = (id: string, raised: boolean) =>
  api.post<MeetingParticipant[]>(`/meetings/${id}/hand`, { raised })

export const listMeetingLinks = (id: string) => api.get<MeetingLink[]>(`/meetings/${id}/links`)

/** The response carries the token. It is the only time it ever will — show it or lose it. */
export const issueMeetingLink = (id: string, body: { label?: string; expiresAt?: string }) =>
  api.post<IssuedMeetingLink>(`/meetings/${id}/links`, body)

export const revokeMeetingLink = (id: string, linkId: string) =>
  api.del<void>(`/meetings/${id}/links/${linkId}`)

/**
 * The chat, polled.
 *
 * `since` is what makes it a poll rather than a re-read: pass the timestamp of the last line
 * you hold and the server sends only what arrived after it. Without it a busy meeting re-sends
 * its whole thread to everybody every few seconds.
 */
export const listMeetingMessages = (id: string, since?: string) =>
  api.get<MeetingMessage[]>(`/meetings/${id}/messages${since ? `?since=${encodeURIComponent(since)}` : ''}`)

export const postMeetingMessage = (id: string, body: string) =>
  api.post<MeetingMessage>(`/meetings/${id}/messages`, { body })

/** The caller's own notepad. Nobody else can read it, including the chair. */
export const getMeetingNote = (id: string) => api.get<MeetingNote>(`/meetings/${id}/note`)

export const saveMeetingNote = (id: string, body: string) =>
  api.put<MeetingNote>(`/meetings/${id}/note`, { body })

/** Start or stop recording. Turning it on records everybody already in the room. */
export const setMeetingRecording = (id: string, enabled: boolean) =>
  api.post<Meeting>(`/meetings/${id}/recording`, { enabled })

export const listMeetingRecordings = (id: string) =>
  api.get<MeetingRecording[]>(`/meetings/${id}/recordings`)

/** A 15-minute URL straight to the object store — the application never streams the file. */
export const meetingPlayback = (id: string, recordingId: string) =>
  api.get<{ url: string }>(`/meetings/${id}/recordings/${recordingId}/playback`)

/** Every speaker's lines, merged on a common clock. */
export const meetingTranscript = (id: string) =>
  api.get<MeetingTranscript>(`/meetings/${id}/transcript`)

/** Ask the council to minute the meeting. Asynchronous: the run is queued when this returns. */
export const requestMeetingAnalysis = (id: string, question?: string) =>
  api.post<MeetingAnalysis>(`/meetings/${id}/analysis`, { question: question ?? null })

export const meetingAnalysis = (id: string) =>
  api.get<MeetingAnalysis>(`/meetings/${id}/analysis`)
